import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection, query, where, orderBy, onSnapshot,
  doc, getDoc
} from "firebase/firestore";
import { useApp } from "../App";

export default function MyListings({ onOpenListing }) {
  const { user, t } = useApp();
  const [tab, setTab] = useState("active");
  const [myListings, setMyListings] = useState([]);
  const [savedListings, setSavedListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "market_listings"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setMyListings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user || tab !== "saved") return;
    const fetchSaved = async () => {
      const snap = await getDoc(doc(db, "users", user.uid));
      if (!snap.exists()) return;
      const savedIds = (snap.data().saved || [])
        .filter((id) => id.startsWith("m_"))
        .map((id) => id.replace("m_", ""));
      if (savedIds.length === 0) {
        setSavedListings([]);
        return;
      }
      const results = [];
      for (const id of savedIds) {
        const lSnap = await getDoc(doc(db, "market_listings", id));
        if (lSnap.exists()) {
          results.push({ id: lSnap.id, ...lSnap.data() });
        }
      }
      setSavedListings(results);
    };
    fetchSaved();
  }, [user, tab]);

  const formatPrice = (price) => {
    if (price === 0 || price === "0") return "Free";
    return `€${price}`;
  };

  const active = myListings.filter((l) => l.status === "active");
  const sold = myListings.filter((l) => l.status === "sold");

  const renderList = (items) => {
    if (loading) {
      return <div className="loading"><div className="spinner" /></div>;
    }
    if (items.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-state-title">
            {tab === "active" && t.noActiveListings}
            {tab === "sold" && t.noSoldListings}
            {tab === "saved" && t.noSavedListings}
          </div>
          {tab === "active" && (
            <div style={{ marginTop: 8, fontSize: 14, color: "var(--text-tertiary)" }}>
              {t.startSelling}
            </div>
          )}
        </div>
      );
    }
    return (
      <div className="my-listings-list">
        {items.map((listing) => (
          <div
            key={listing.id}
            className="my-listing-card"
            onClick={() => onOpenListing(listing)}
          >
            {listing.imageUrl ? (
              <img
                className="my-listing-image"
                src={listing.imageUrl}
                alt={listing.title}
              />
            ) : (
              <div className="my-listing-image" style={{ background: "var(--offwhite)" }} />
            )}
            <div className="my-listing-body">
              <div className="my-listing-title">{listing.title}</div>
              <div className="my-listing-price">{formatPrice(listing.price)}</div>
              <div className="my-listing-meta">{listing.location}</div>
              {listing.status === "sold" && (
                <div style={{
                  display: "inline-block",
                  marginTop: 4,
                  background: "var(--navy)",
                  color: "var(--cream)",
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "2px 10px",
                  borderRadius: 20,
                }}>
                  Sold
                </div>
              )}
              {listing.movingSale && (
                <div className="moving-sale-tag">{t.movingSale}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="my-listings-page">
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 0 }}>
        <div className="page-header-row">
          <span className="page-header-wordmark">{t.myListings}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-bar">
        <button
          className={`tab-btn ${tab === "active" ? "active" : ""}`}
          onClick={() => setTab("active")}
        >
          {t.active} {active.length > 0 && `(${active.length})`}
        </button>
        <button
          className={`tab-btn ${tab === "sold" ? "active" : ""}`}
          onClick={() => setTab("sold")}
        >
          {t.sold} {sold.length > 0 && `(${sold.length})`}
        </button>
        <button
          className={`tab-btn ${tab === "saved" ? "active" : ""}`}
          onClick={() => setTab("saved")}
        >
          {t.saved}
        </button>
      </div>

      {tab === "active" && renderList(active)}
      {tab === "sold" && renderList(sold)}
      {tab === "saved" && renderList(savedListings)}
    </div>
  );
}
